/**
 * 입력 폼용 클라이언트측 검증·정규화 헬퍼
 *
 * 모든 영업 리드 폼(QuoteForm Step4 / AgencyForm / PrivateQuoteForm)에서 공통으로
 * 사용하여 다음을 차단한다.
 *
 *  · 길이 초과 (오버플로우)
 *  · 제어문자 / 비가시 유니코드
 *  · 스크립트·HTML 인젝션 휴리스틱 (XSS 1차 방어)
 *
 * React가 JSX 렌더 시 자동 이스케이프하므로 위젯 자체에는 즉시적 XSS 위험이 없으나,
 * 입력값은 백엔드를 거쳐 운영자 콘솔/이메일/CRM 등에서 다시 표시되므로 1차로 클라이언트
 * 단계에서 명백한 공격성 입력을 거부한다. 서버측 검증은 별도 필수.
 */

/**
 * 필드별 허용 길이 (문자 수). 너무 길면 오버플로우/저장소 부담.
 */
export const MAX_LENGTHS = {
  company: 100,
  name: 50,
  email: 254,
  phone: 30,
  message: 2000,
} as const;

// 제어문자: U+0000-U+0008, U+000B, U+000C, U+000E-U+001F, U+007F
// (\t=U+0009, \n=U+000A, \r=U+000D는 textarea 허용 → 제외)
// `g` 플래그를 .test()에 같이 쓰면 lastIndex가 누적되어 결과가 흔들리므로
// replace 전용과 test 전용 인스턴스를 분리.
const CONTROL_CHARS_REPLACE_RE =
  /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;
const CONTROL_CHARS_TEST_RE =
  /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/;

// 비가시·방향 우회 유니코드 (zero-width, RLO/LRO 등 표시 위장 공격 차단)
const ZERO_WIDTH_REPLACE_RE =
  /[​-\u200F\u202A-\u202E⁠-⁯﻿]/g;

// 스크립트·HTML 인젝션 휴리스틱. 정상 한국어 입력에는 등장하지 않는 패턴.
const INJECTION_RE =
  /<\s*\/?\s*(script|iframe|object|embed|svg|img|link|style|meta|base|form)\b|javascript\s*:|data\s*:\s*text\/html|vbscript\s*:|on\w+\s*=/i;

export type ValidationReason = 'too_long' | 'control_chars' | 'suspicious';

export interface ValidationIssue {
  field: string;
  reason: ValidationReason;
}

/**
 * 입력 즉시 호출 — 제어문자·비가시 유니코드를 제거해 표시·저장 직전에 정규화.
 * 사용자 친화적 처리 (반려가 아닌 자동 정정).
 */
export function normalizeInput(raw: string): string {
  return raw
    .replace(CONTROL_CHARS_REPLACE_RE, '')
    .replace(ZERO_WIDTH_REPLACE_RE, '');
}

/**
 * 단일 필드 안전성 검사.
 * 정규화된 값과 위반 사유(있다면)를 반환.
 */
export function checkFieldSafety(
  raw: string,
  field: string,
  maxLength: number
): { value: string; issue: ValidationIssue | null } {
  const value = normalizeInput(raw);
  if (value.length > maxLength) {
    return { value, issue: { field, reason: 'too_long' } };
  }
  if (CONTROL_CHARS_TEST_RE.test(raw)) {
    return { value, issue: { field, reason: 'control_chars' } };
  }
  if (INJECTION_RE.test(value)) {
    return { value, issue: { field, reason: 'suspicious' } };
  }
  return { value, issue: null };
}

/**
 * 연락처 객체 일괄 검수. 제출 직전 호출.
 */
export interface ContactLike {
  company: string;
  name: string;
  email: string;
  phone: string;
  message?: string;
}

export function validateContact(contact: ContactLike): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const fields: Array<[keyof typeof MAX_LENGTHS, string | undefined]> = [
    ['company', contact.company],
    ['name', contact.name],
    ['email', contact.email],
    ['phone', contact.phone],
    ['message', contact.message ?? ''],
  ];
  for (const [field, value] of fields) {
    if (typeof value !== 'string') continue;
    const { issue } = checkFieldSafety(value, field, MAX_LENGTHS[field]);
    if (issue) issues.push(issue);
  }
  return issues;
}

const FIELD_LABELS: Record<keyof typeof MAX_LENGTHS, string> = {
  company: '회사명',
  name: '담당자명',
  email: '이메일',
  phone: '연락처',
  message: '요청사항',
};

const REASON_LABELS: Record<ValidationReason, string> = {
  too_long: '입력 길이 초과',
  control_chars: '허용되지 않는 문자 포함',
  suspicious: '안전하지 않은 입력 (스크립트·HTML 의심)',
};

export function describeIssue(issue: ValidationIssue): string {
  const fieldLabel =
    (FIELD_LABELS as Record<string, string>)[issue.field] ?? issue.field;
  return `${fieldLabel}: ${REASON_LABELS[issue.reason]}`;
}
